import { Button } from '@/components/ui/button'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { Controller, type Control } from 'react-hook-form'
import { MappingSelect } from './mapping-select'
import type {
  AbsenceImportFormValues,
  AnalyzePayload,
  AbsenceImportColumn,
  AbsenceImportColumnMapping,
  AbsenceImportPreviewRow,
} from '../types'

interface Props {
  control: Control<AbsenceImportFormValues>
  columnMapping: AbsenceImportColumnMapping
  columns: AbsenceImportColumn[]
  previewRows: AbsenceImportPreviewRow[]
  parseWarnings: string[]
  sheetName: string
  headerRow: number | null
  analysis: AnalyzePayload | null
  analyzing: boolean
  canAnalyze: boolean
  onAnalyze(): void
  onBack(): void
}

export function StepColumnMapping({
  control,
  columnMapping,
  columns,
  previewRows,
  parseWarnings,
  sheetName,
  headerRow,
  analysis,
  analyzing,
  canAnalyze,
  onAnalyze,
  onBack,
}: Props) {
  const translations = useTranslate({
    sheetInfo: 'settings.absence.import.sheet-info',
    parseWarnings: 'settings.absence.import.parse-warnings',
    previewRows: 'settings.absence.import.preview-rows',
    employeeNumber: 'settings.absence.import.employee-number',
    absenceType: 'settings.absence.import.absence-type',
    fromDate: 'settings.absence.import.from-date',
    toDate: 'settings.absence.import.to-date',
    selectColumn: 'settings.absence.import.select-column',
    stats: 'settings.absence.import.stats',
    rowIssues: 'settings.absence.import.row-issues',
    validRows: 'settings.absence.import.valid-rows',
    noUserMatches: 'settings.absence.import.no-user-matches',
    next: 'settings.absence.absence-type.next',
    back: 'common.back',
  })

  return (
    <div className="grid gap-4">
      <div className="rounded-md border border-stone-200 p-3 text-sm text-stone-700">
        <p>
          {translations.sheetInfo}: {sheetName || '-'}
          {headerRow ? `, row ${headerRow}` : ''}
        </p>

        {!!parseWarnings.length && (
          <div className="mt-2">
            <p className="font-medium">{translations.parseWarnings}</p>
            <ul className="list-disc pl-6">
              {parseWarnings.map(warning => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Controller
          control={control}
          name="columnMapping.employeeNumberColumn"
          render={({ field }) => (
            <MappingSelect
              label={translations.employeeNumber}
              value={field.value ?? columnMapping.employeeNumberColumn}
              columns={columns}
              placeholder={translations.selectColumn}
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="columnMapping.absenceTypeColumn"
          render={({ field }) => (
            <MappingSelect
              label={translations.absenceType}
              value={field.value ?? columnMapping.absenceTypeColumn}
              columns={columns}
              placeholder={translations.selectColumn}
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="columnMapping.fromDateColumn"
          render={({ field }) => (
            <MappingSelect
              label={translations.fromDate}
              value={field.value ?? columnMapping.fromDateColumn}
              columns={columns}
              placeholder={translations.selectColumn}
              onChange={field.onChange}
            />
          )}
        />

        <Controller
          control={control}
          name="columnMapping.toDateColumn"
          render={({ field }) => (
            <MappingSelect
              label={translations.toDate}
              value={field.value ?? columnMapping.toDateColumn}
              columns={columns}
              placeholder={translations.selectColumn}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onBack}>
          {translations.back}
        </Button>

        <Button size="sm" disabled={!canAnalyze || analyzing} onClick={onAnalyze}>
          {translations.next}
        </Button>
      </div>

      <div className="rounded-md border border-stone-200 p-3">
        <p className="mb-2 text-sm font-medium">{translations.previewRows}</p>

        <div className="max-h-56 overflow-auto">
          <table className="w-full min-w-[640px] text-left text-xs">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="px-2 py-2 font-semibold text-stone-700">Row</th>
                {columns.map(column => (
                  <th
                    key={column.index}
                    className="px-2 py-2 font-semibold text-stone-700"
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {previewRows.map(row => (
                <tr
                  key={row.sourceRow}
                  className="border-b border-stone-100 align-top"
                >
                  <td className="px-2 py-2">{row.sourceRow}</td>
                  {columns.map((column, idx) => (
                    <td key={`${row.sourceRow}-${column.index}`} className="px-2 py-2">
                      {row.values[idx] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {analysis && (
        <div className="grid gap-4 rounded-md border border-stone-200 p-3">
          <p className="text-sm font-medium">{translations.stats}</p>
          <div className="grid gap-1 text-sm text-stone-700 md:grid-cols-2">
            <p>Total rows: {analysis.totalRows}</p>
            <p>Matched users: {analysis.matchedRowsCount}</p>
            <p>Valid rows: {analysis.validRowsCount}</p>
            <p>Invalid rows: {analysis.invalidRowsCount}</p>
          </div>

          {!!analysis.blockingMessage && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {analysis.blockingMessage || translations.noUserMatches}
            </div>
          )}

          {!!analysis.rowIssues.length && (
            <div>
              <p className="mb-2 text-sm font-medium">{translations.rowIssues}</p>
              <div className="max-h-44 overflow-auto rounded border border-stone-200">
                <table className="w-full text-xs">
                  <tbody>
                    {analysis.rowIssues.map(issue => (
                      <tr key={issue.sourceRow} className="border-b border-stone-100">
                        <td className="w-20 px-2 py-2 font-medium">Row {issue.sourceRow}</td>
                        <td className="px-2 py-2">{issue.messages.join(' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!analysis.validRowsPreview.length && (
            <div>
              <p className="mb-2 text-sm font-medium">{translations.validRows}</p>
              <div className="max-h-44 overflow-auto rounded border border-stone-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-stone-200 bg-stone-50 text-left">
                      <th className="px-2 py-2">Row</th>
                      <th className="px-2 py-2">{translations.employeeNumber}</th>
                      <th className="px-2 py-2">User</th>
                      <th className="px-2 py-2">{translations.absenceType}</th>
                      <th className="px-2 py-2">{translations.fromDate}</th>
                      <th className="px-2 py-2">{translations.toDate}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.validRowsPreview.map(row => (
                      <tr key={row.sourceRow} className="border-b border-stone-100">
                        <td className="px-2 py-2">{row.sourceRow}</td>
                        <td className="px-2 py-2">{row.employeeNumber}</td>
                        <td className="px-2 py-2">{row.userName}</td>
                        <td className="px-2 py-2">{row.absenceTypeValue}</td>
                        <td className="px-2 py-2">{row.fromDate}</td>
                        <td className="px-2 py-2">{row.toDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
